import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import CoursePage from "../pages/student/CoursePage";
import StagePage from "../pages/student/StagePage";
import BoardPage from "../pages/board/BoardPage";
import { AppLayout } from "./AppLayout";
import AvatarPage from "../pages/student/AvatarPage";
import EntryPage from "../pages/public/EntryPage";
import OpsConsolePage from "../pages/ops/OpsConsolePage";
import OrgReportPage from "../pages/org/OrgReportPage";
import FootprintsPage from "../pages/student/FootprintsPage";
import GalleryPage from "../pages/student/GalleryPage";
import ReplayPage from "../pages/student/ReplayPage";
import RoomPage from "../pages/student/RoomPage";
import TeacherConsolePage from "../pages/teacher/TeacherConsolePage";
import HomePage from "../pages/student/HomePage";
import { PlaceholderScreen } from "./PlaceholderScreen";
import PlaygroundSettingsPage from "../pages/teacher/PlaygroundSettingsPage";
import { RequireSession } from "./RequireSession";
import TeacherHomePage from "../pages/teacher/TeacherHomePage";
import DeepLinkPage from "../pages/teacher/DeepLinkPage";
import NetcheckPage from "../pages/teacher/NetcheckPage";
import UploadPage from "../pages/student/UploadPage";
import MicrobitPage from "../pages/student/MicrobitPage";
import SharePage from "../pages/share/SharePage";
import { screenRoutes } from "./table";
import type { RouteRole } from "./table";

const REAL: Record<string, ReactElement> = {
  "/room": <RoomPage />,
  "/launch": <EntryPage door="A" />,
  "/sso": <EntryPage door="B" />,
  "/home": <HomePage />,
  "/avatar": <AvatarPage />,
  "/upload": <UploadPage />,
  "/course": <CoursePage />,
  "/stage": <StagePage />,
  "/gallery": <GalleryPage />,
  "/me": <FootprintsPage />,
  "/microbit": <MicrobitPage />,
  "/replay/:id": <ReplayPage />,
  "/teacher": <TeacherHomePage />,
  "/teacher/course/:courseId/playground": <PlaygroundSettingsPage />,
  "/teacher/room/:roomId": <TeacherConsolePage tab="room" />,
  "/teacher/room/:roomId/teams": <TeacherConsolePage tab="teams" />,
  "/teacher/room/:roomId/script": <TeacherConsolePage tab="script" />,
  "/teacher/room/:roomId/sandbox": <TeacherConsolePage tab="sandbox" />,
  "/teacher/room/:roomId/assess": <TeacherConsolePage tab="assess" />,
  "/teacher/room/:roomId/relay": <TeacherConsolePage tab="relay" />,
  "/teacher/room/:roomId/video": <TeacherConsolePage tab="video" />,
  "/teacher/room/:roomId/exchange": <TeacherConsolePage tab="exchange" />,
  "/board/:roomId": <BoardPage />,
  "/share/:token": <SharePage />,
  "/share": <SharePage />,
  "/teacher/deeplink": <DeepLinkPage />,
  "/teacher/netcheck": <NetcheckPage />,
  // FS-15-01 룸 모니터가 첫 탭(P2-S4)
  "/ops": <Navigate to="/ops/monitor" replace />,
  "/ops/monitor": <OpsConsolePage tab="monitor" />,
  "/ops/catalog": <OpsConsolePage tab="catalog" />,
  "/ops/modes": <OpsConsolePage tab="modes" />,
  "/ops/config": <OpsConsolePage tab="config" />,
  "/ops/assets": <OpsConsolePage tab="assets" />,
  "/ops/stages": <OpsConsolePage tab="stages" />,
  "/ops/goals": <OpsConsolePage tab="goals" />,
  "/org/report": <OrgReportPage />,
};

const byRole = (role: RouteRole): RouteObject[] =>
  screenRoutes
    .filter((r) => r.role === role)
    .map((r) => ({ path: r.path, element: REAL[r.path] ?? <PlaceholderScreen route={r} /> }));

/** 라우트 정의 — main 은 브라우저 라우터, 테스트는 메모리 라우터로 쓴다. */
export const routes: RouteObject[] = [
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/launch" replace /> },
      ...byRole("public"),
      ...(["student", "teacher", "operator", "org", "any"] as const).map((role) => ({
        element: <RequireSession role={role} />,
        children: byRole(role),
      })),
      { path: "*", element: <Navigate to="/launch" replace /> },
    ],
  },
];

/** React Router v7 동작을 미리 켠다(경고 제거 · 업그레이드 대비). */
export const routerFuture = {
  v7_relativeSplatPath: true,
  v7_fetcherPersist: true,
  v7_normalizeFormMethod: true,
  v7_partialHydration: true,
  v7_skipActionErrorRevalidation: true,
} as const;
